import React, { useEffect, useState, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { v4 as uuid } from "uuid";
import "../../../../assets/css/edit-manage.css";
import { useDispatch, useSelector } from "react-redux";
import OuterDraggable from "react-draggable";
import LoadingOverlay from "react-loading-overlay";
import Loader from "../../../common/components/Loader";
import actions from "../../actions";
import ConfirmationModal from "../../../common/components/ConfirmationModal";
import _ from "lodash";
import Portal from "../../../common/components/Portal";
import { HeaderDraggableLine } from "./HeaderDraggableLine";
import { EllipsisTooltipText } from "../../../common/components/EllipsisTextWithTooltip";
import PreviewModal from "../../../manageHeadings/components/PreviewModal";
import ReactTooltip from "react-tooltip";

const HeaderConfigModal = ({ mfilter = {}, headerConfigData = [], onClose, onSubmit, isReadOnly = false, isCoreReportCreate = false }) => {
    const dispatch = useDispatch();
    const { getHeaderKeyListResponse } = useSelector(state => state.smartChartReducer);
    const [transform, setTransform] = useState({ left: 0, top: 0 });
    const [droppedItems, setDroppedItems] = useState([]);
    const [items, setItems] = useState([]);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [previewData, setPreviewData] = useState([]);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const MAX_PER_ROW = 4;

    const custom_header_keys = droppedItems
        .map(item => ({
            value: item?.value,
            isText: item?.isText ?? false
        }))
        .filter(obj => obj.value);

    const buildRows = (items = []) => {
        const rows = items.reduce((acc, item) => {
            if (!acc.length || acc[acc.length - 1].length === MAX_PER_ROW) {
                acc.push([]);
            }

            acc[acc.length - 1].push(item);
            return acc;
        }, []);

        if (rows.length === 0 || rows[rows.length - 1].length >= MAX_PER_ROW) {
            rows.push([]);
        }

        return rows;
    };
    const rows = buildRows(droppedItems);

    useEffect(() => {
        dispatch(actions?.getHeaderKeyList(isCoreReportCreate ? { core_report_filters: mfilter } : { filters: mfilter }));
    }, []);

    useEffect(() => {
        if (headerConfigData?.length) {
            setDroppedItems(
                headerConfigData.map(itemData => ({
                    id: uuid(),
                    content: itemData?.value,
                    value: itemData?.value,
                    isText: itemData?.isText ?? false
                }))
            );
        }
    }, []);

    useEffect(() => {
        if (getHeaderKeyListResponse?.data?.user_keys?.length) {
            const textInputItem = {
                id: uuid(),
                content: "Text input",
                value: "",
                isText: true
            };
            const keyItems = getHeaderKeyListResponse.data.user_keys.map(key => ({
                id: uuid(),
                content: key,
                value: key,
                isText: false
            }));
            setItems([textInputItem, ...keyItems]);
        }
        if (Object.keys(getHeaderKeyListResponse?.data?.user_keys_mapping ?? {})?.length) {
            setPreviewData(getHeaderKeyListResponse?.data?.user_keys_mapping);
        }
    }, [getHeaderKeyListResponse]);

    const onCancel = () => {
        if (_.isEqual(headerConfigData, custom_header_keys)) {
            setShowConfirmationModal(false);
            onClose();
        } else {
            setShowConfirmationModal(true);
        }
    };

    const onCancelRef = useRef(onCancel);
    useEffect(() => {
        onCancelRef.current = onCancel;
    });

    useEffect(() => {
        const handleKeyDown = e => {
            if (e.key === "Escape") {
                e.preventDefault();
                e.stopImmediatePropagation();
                onCancelRef.current();
            }
        };
        window.addEventListener("keydown", handleKeyDown, true);
        return () => window.removeEventListener("keydown", handleKeyDown, true);
    }, []);

    const [isDraggingFromItems, setIsDraggingFromItems] = useState(false);

    const onDragStart = start => {
        setIsDraggingFromItems(start.source.droppableId === "ITEMS");
    };

    const onDragEnd = result => {
        setIsDraggingFromItems(false);
        const { source, destination } = result;
        if (!destination) return;

        const srcId = source?.droppableId;
        const destId = destination?.droppableId;

        if (srcId === destId && srcId?.startsWith("droppable-")) {
            const rowIndex = parseInt(srcId.split("droppable-")[1]);
            const globalSrc = rowIndex * MAX_PER_ROW + source?.index;
            const globalDest = rowIndex * MAX_PER_ROW + destination?.index;
            if (globalSrc === globalDest) return;

            setDroppedItems(prev => {
                const newArr = [...prev];
                const [removed] = newArr?.splice(globalSrc, 1);
                newArr?.splice(globalDest, 0, removed);
                return newArr;
            });
            return;
        }

        if (srcId === "ITEMS") {
            const destRowIndex = parseInt(destId.split("droppable-")[1]);
            const draggedItem = items?.[source?.index];
            const newItem = {
                id: uuid(),
                content: draggedItem?.content,
                value: draggedItem?.value,
                isText: draggedItem?.isText || false
            };
            const globalInsertIndex = destRowIndex * MAX_PER_ROW + destination.index;

            setDroppedItems(prev => {
                const newArr = [...prev];
                newArr?.splice(globalInsertIndex, 0, newItem);
                return newArr;
            });
            return;
        }

        if (srcId.startsWith("droppable-") && destId.startsWith("droppable-")) {
            const srcRowIndex = parseInt(srcId.split("droppable-")[1]);
            const destRowIndex = parseInt(destId.split("droppable-")[1]);

            const globalSrc = srcRowIndex * MAX_PER_ROW + source.index;
            const globalDest = destRowIndex * MAX_PER_ROW + destination.index;
            if (globalSrc === globalDest) return;

            setDroppedItems(prev => {
                const newArr = [...prev];
                const [removed] = newArr.splice(globalSrc, 1);
                const adjustedDest = globalSrc < globalDest ? globalDest - 1 : globalDest;
                newArr.splice(adjustedDest, 0, removed);
                return newArr;
            });
        }
    };

    const deleteItem = (rowIndex, colIndex) => {
        const globalIndex = rowIndex * MAX_PER_ROW + colIndex;
        setDroppedItems(prev => prev.filter((_, i) => i !== globalIndex));
    };

    const handleChangeInput = (e, rowIndex, colIndex) => {
        const globalIndex = rowIndex * MAX_PER_ROW + colIndex;
        const { value } = e.target;
        setDroppedItems(prev => {
            const newArr = [...prev];
            newArr[globalIndex] = { ...newArr[globalIndex], value };
            return newArr;
        });
    };

    const handleSubmit = async () => {
        onSubmit && onSubmit(custom_header_keys);
    };

    const handleDrag = (e, ui) => setTransform({ left: ui.x, top: ui.y });

    const renderConfirmationModal = () => {
        if (!showConfirmationModal) return null;
        return (
            <Portal
                body={
                    <ConfirmationModal
                        type="cancel"
                        heading={"Do you want to clear and lose all changes?"}
                        message={"This action cannot be reverted, are you sure that you need to cancel?"}
                        onNo={() => setShowConfirmationModal(false)}
                        onYes={() => {
                            setShowConfirmationModal(false);
                            onClose();
                        }}
                    />
                }
                onCancel={() => setShowConfirmationModal(false)}
            />
        );
    };

    const renderPreviewModal = () => {
        if (!showPreviewModal) return null;
        return (
            <Portal
                body={
                    <PreviewModal
                        previewData={() => [
                            (droppedItems || [])?.map(item => (item?.isText ? (item?.value ?? "") : (previewData?.[item?.value] ?? "")))
                        ]}
                        onCancel={() => setShowPreviewModal(false)}
                        isSmartChart={true}
                    />
                }
                onCancel={() => setShowPreviewModal(false)}
            />
        );
    };
    const textItem = items.filter(elem => elem.isText);
    const headerItems = items.filter(elem => !elem.isText);

    return (
        <div
            className="modal modal-view assign-init-modal heading-form-modal edt-mng-dflex smartchart-header-config-modal"
            id="headingFormModalId"
            style={{ display: "block" }}
            tabIndex="-1"
        >
            <LoadingOverlay active={getHeaderKeyListResponse?.loading} spinner={<Loader />} fadeSpeed={10}>
                <div className="modal-dialog region-mng" role="document">
                    <OuterDraggable handle=".draggable" cancel=".not-draggable" onDrag={handleDrag}>
                        <div className="modal-content outerModal_draggable" style={transform}>
                            <div className="modal-header draggable">
                                <h5 className="modal-title">Page Header Configuration</h5>
                                <div className="edit-icn-bx select-st">
                                    <button type="button" className="close" onClick={onCancel}>
                                        <span aria-hidden="true">&times;</span>
                                    </button>
                                </div>
                            </div>

                            <div className="modal-body not-draggable">
                                <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
                                    <div className="dtl-sec col-md-12">
                                        <div className="edt-mng-dflex mt-2 edit-hd-manage-outer">
                                            <div className="top-area">
                                                {!isReadOnly && (
                                                    <Droppable droppableId="ITEMS" isDropDisabled={true} type="item">
                                                        {provided => (
                                                            <div className="lft-area" ref={provided.innerRef} {...provided.droppableProps}>
                                                                {textItem.map((elem, index) => (
                                                                    <Draggable
                                                                        key={elem.id}
                                                                        draggableId={elem.id}
                                                                        index={index}
                                                                        isDragDisabled={isReadOnly}
                                                                    >
                                                                        {(provided, snapshot) => (
                                                                            <>
                                                                                <div
                                                                                    className="item text-input-item"
                                                                                    ref={provided.innerRef}
                                                                                    {...provided.draggableProps}
                                                                                    {...provided.dragHandleProps}
                                                                                    style={provided.draggableProps.style}
                                                                                >
                                                                                    <div className="item-txt">
                                                                                        <EllipsisTooltipText
                                                                                            text={elem?.content}
                                                                                            className={"overflow-text-wrapper"}
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                                {snapshot.isDragging && (
                                                                                    <div className="item text-input-item">
                                                                                        <div className="item-txt">
                                                                                            {" "}
                                                                                            <EllipsisTooltipText
                                                                                                text={elem?.content}
                                                                                                className={"overflow-text-wrapper"}
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </Draggable>
                                                                ))}

                                                                <p className="header-keys-label mt-2 mb-1">HEADER KEYS</p>

                                                                {headerItems.map((elem, index) => (
                                                                    <Draggable
                                                                        key={elem.id}
                                                                        draggableId={elem.id}
                                                                        index={textItem.length + index}
                                                                        isDragDisabled={isReadOnly}
                                                                    >
                                                                        {(provided, snapshot) => (
                                                                            <>
                                                                                <div
                                                                                    className="item"
                                                                                    ref={provided.innerRef}
                                                                                    {...provided.draggableProps}
                                                                                    {...provided.dragHandleProps}
                                                                                    style={provided.draggableProps.style}
                                                                                >
                                                                                    <div className="item-txt">
                                                                                        <EllipsisTooltipText
                                                                                            text={elem?.content}
                                                                                            className={"overflow-text-wrapper"}
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                                {snapshot.isDragging && (
                                                                                    <div className="item">
                                                                                        <div className="item-txt">
                                                                                            {" "}
                                                                                            <EllipsisTooltipText
                                                                                                text={elem?.content}
                                                                                                className={"overflow-text-wrapper"}
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </Draggable>
                                                                ))}
                                                                {provided.placeholder}
                                                            </div>
                                                        )}
                                                    </Droppable>
                                                )}

                                                <div className={`right-area ${isReadOnly ? "read-only" : ""}`}>
                                                    <div className="main-area">
                                                        <div className="d-flex justify-content-end">
                                                            <button
                                                                className={`btn btn-cancel btn-preview smartchart-btn-preview mr-2 mb-2 ${!droppedItems?.length ? "cursor-disabled" : ""}`}
                                                                type="button"
                                                                onClick={() => setShowPreviewModal(true)}
                                                                disabled={!droppedItems?.length}
                                                                data-for={"header-config"}
                                                                data-tip={!droppedItems?.length ? "Add items to enable preview" : ""}
                                                            >
                                                                Preview
                                                            </button>
                                                            <ReactTooltip
                                                                id="header-config"
                                                                effect="solid"
                                                                place="bottom"
                                                                backgroundColor="#007bff"
                                                            />
                                                        </div>
                                                        <div className="item">
                                                            <HeaderDraggableLine
                                                                rows={rows}
                                                                deleteItem={deleteItem}
                                                                handleChangeInput={handleChangeInput}
                                                                isDraggingFromItems={isDraggingFromItems}
                                                                droppedItems={droppedItems}
                                                                isReadOnly={isReadOnly}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </DragDropContext>
                            </div>

                            {isReadOnly ? null : (
                                <div className="modal-footer">
                                    <div className="btn-sec">
                                        <div className="text-right btnOtr edit-cmn-btn">
                                            <button type="button" className="btn btn-secondary mr-1" onClick={onCancel}>
                                                Cancel
                                            </button>
                                            <button type="button" className="btn btn-primary" onClick={handleSubmit}>
                                                Submit
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </OuterDraggable>
                </div>
            </LoadingOverlay>
            {renderConfirmationModal()}
            {renderPreviewModal()}
        </div>
    );
};

export default HeaderConfigModal;
